/*
 * This file is part of SketchBand.
 * Copyright (C) 2016  Mikael Brockman
 * 
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 * 
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 * 
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

let audioContext =
  new (window.AudioContext || window.webkitAudioContext)()

let gainNode = audioContext.createGain()
let gainConnected = false

requestAnimationFrame(() => {
  let x = localStorage.getItem("gain")
  gainNode.gain.value = x === null ? 1 : +x
})

let secondsPerBar = ({ beatsPerBar, bpm }) =>
  (60 / bpm) * beatsPerBar

export let resumeAudio = () =>
  audioContext.state === "suspended"
    ? audioContext.resume()
    : Promise.resolve()

let connectGainNode = () => {
  if (!gainConnected) {
    gainNode.connect(audioContext.destination)
    gainConnected = true
  }
}

export let trackAudioTime = (
  context,
  f,
  { startAt = 0, stopAt = null } = {}
) => {
  let cancelled = false
  let audioT0 = audioContext.currentTime
  let wallT0 = performance.now() / 1000
  let frameId = null

  let elapsedSeconds = () => {
    let audioElapsed = audioContext.currentTime - audioT0
    if (audioContext.state === "running" && audioElapsed > 0) {
      return audioElapsed
    }
    return performance.now() / 1000 - wallT0
  }

  let frame = () => {
    if (cancelled) return

    let elapsed = elapsedSeconds()
    let absoluteSeconds = startAt + elapsed
    let absoluteBars = absoluteSeconds / secondsPerBar(context)

    if (stopAt !== null && absoluteSeconds >= stopAt) {
      let clampedBars = stopAt / secondsPerBar(context)
      f(stopAt, clampedBars, { done: true })
      return
    }

    f(absoluteSeconds, absoluteBars, { done: false })
    frameId = requestAnimationFrame(frame)
  }
  frameId = requestAnimationFrame(frame)

  return () => {
    cancelled = true
    if (frameId !== null) {
      cancelAnimationFrame(frameId)
    }
  }
}

export let toggleMute = () => {
  let gain = gainNode.gain.value > 0 ? 0 : 1
  gainNode.gain.value = gain
  localStorage.setItem("gain", gain)
}

export let playSchedule =
  async (
    samples,
    { schedule },
    { startAt = 0, stopAt = null } = {}
  ) => {
    connectGainNode()

    let sources = schedule.events.map(({ play }) => {
      let { chord, delay } = play
      let sample = samples[chord.name]
      if (!sample) return null

      let offset = Math.max(startAt - delay, 0)
      if (offset >= sample.duration) return null

      let startsAt = Math.max(delay - startAt, 0)
      let duration = sample.duration - offset

      if (stopAt !== null) {
        let eventEndsAt = delay + sample.duration
        if (delay >= stopAt || eventEndsAt <= startAt) {
          return null
        }
        duration = Math.min(duration, stopAt - Math.max(delay, startAt))
      }

      if (duration <= 0) return null

      return playBuffer(sample, startsAt, offset, duration)
    }).filter(Boolean)

    return {
      stop() {
        sources.forEach(source => {
          try {
            source.stop()
          } catch (error) {
            if (error.name !== "InvalidStateError") {
              throw error
            }
          }
        })
      }
    }
  }

let playBuffer = (buffer, delay, offset = 0, duration = buffer.duration - offset) => {
  let source = audioContext.createBufferSource()
  source.buffer = buffer
  source.connect(gainNode)
  source.start(audioContext.currentTime + delay, offset, duration)
  return source
}
